/**
 * Superadmin — Consultations atomiques
 *
 * Per user spec: "atomic 2 patient have double consultation with diferente drs"
 *
 * Pattern: Optimistic Locking + Saga
 * - Un patient peut avoir plusieurs consultations simultanées avec différents praticiens
 * - Chaque consultation est un "brouillon" indépendant (pas de lock partagé)
 * - Le merge/commit vérifie l'absence de conflit sur l'entité patient
 * - Version vector (consultation.version) incrémenté à chaque save
 * - Si conflit détecté, l'utilisateur voit un modal de résolution
 *
 * Backend pattern:
 *   POST /api/consultations
 *   { patientId, doctorId, type, startedAt, expectedDurationMin }
 *   → crée consultation avec version=1
 *   → emit ConsultationCreated event (saga pattern)
 *   → le saga orchestre les sous-événements (prescription, lab, fse)
 *   → chaque sous-événement est un step indépendant
 *
 * Si une autre consultation est active sur le même patient par un autre médecin :
 *   → allowed (concurrent) avec banner d'avertissement
 *   → si lock explicite, refusé
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Activity, Plus, Search, Users, AlertTriangle, CheckCircle2, XCircle, GitBranch, Lock, Hash, Clock, User, Stethoscope, History, Zap } from "lucide-react";
import { MOCK_DOCTORS, SPECIALTY_LABEL } from "@/lib/referential/doctor-cotations";

export const Route = createFileRoute("/admin/consultations")({
  component: ConsultationsAdmin,
});

interface Consultation {
  id: string;
  patientMrn: string;
  patientName: string;
  doctorId: string;
  doctorName: string;
  doctorSpecialty: string;
  type: "consultation" | "surgery" | "eeg" | "follow-up" | "emergency";
  startedAt: string;
  expectedDurationMin: number;
  status: "DRAFT" | "IN_PROGRESS" | "SIGNED" | "AMENDED" | "CANCELLED";
  version: number;
  hasConflict: boolean;
  concurrentWith?: string[]; // IDs of concurrent consultations
  lockedBy?: string;        // doctorId who has explicit lock
  events: number;           // number of sub-events in saga
  lastUpdated: string;
}

const CONSULTATIONS: Consultation[] = [
  { id: "c-001", patientMrn: "P1234", patientName: "Mia Miamai", doctorId: "dr-001", doctorName: "Dr Ali BENABDERRAZZAK", doctorSpecialty: "ANES", type: "consultation", startedAt: "2026-06-19 09:15", expectedDurationMin: 30, status: "IN_PROGRESS", version: 3, hasConflict: true, concurrentWith: ["c-002", "c-003"], events: 5, lastUpdated: "2026-06-19 09:32" },
  { id: "c-002", patientMrn: "P1234", patientName: "Mia Miamai", doctorId: "dr-003", doctorName: "Dr Mehdi EL FASSI", doctorSpecialty: "NEURO", type: "consultation", startedAt: "2026-06-19 09:20", expectedDurationMin: 45, status: "IN_PROGRESS", version: 2, hasConflict: true, concurrentWith: ["c-001", "c-003"], events: 3, lastUpdated: "2026-06-19 09:28" },
  { id: "c-003", patientMrn: "P1234", patientName: "Mia Miamai", doctorId: "dr-002", doctorName: "Dr Sophie MARTIN", doctorSpecialty: "CARD", type: "eeg", startedAt: "2026-06-19 09:25", expectedDurationMin: 60, status: "IN_PROGRESS", version: 1, hasConflict: true, concurrentWith: ["c-001", "c-002"], lockedBy: "dr-002", events: 1, lastUpdated: "2026-06-19 09:25" },
  { id: "c-004", patientMrn: "P2451", patientName: "Ahmed Tazi", doctorId: "dr-004", doctorName: "Dr Yasmine AMRANI", doctorSpecialty: "GYN", type: "follow-up", startedAt: "2026-06-19 10:00", expectedDurationMin: 20, status: "DRAFT", version: 1, hasConflict: false, events: 0, lastUpdated: "2026-06-19 09:55" },
  { id: "c-005", patientMrn: "P4422", patientName: "Karim Berrada", doctorId: "dr-006", doctorName: "Dr Leila TAZI", doctorSpecialty: "OPHT", type: "consultation", startedAt: "2026-06-19 10:15", expectedDurationMin: 30, status: "IN_PROGRESS", version: 4, hasConflict: false, events: 7, lastUpdated: "2026-06-19 10:42" },
  { id: "c-006", patientMrn: "P6710", patientName: "Sara El Fassi", doctorId: "dr-005", doctorName: "Dr Hassan BERRADA", doctorSpecialty: "PEDI", type: "emergency", startedAt: "2026-06-19 11:00", expectedDurationMin: 60, status: "IN_PROGRESS", version: 2, hasConflict: false, events: 2, lastUpdated: "2026-06-19 11:18" },
  { id: "c-007", patientMrn: "P0983", patientName: "Omar Chraïbi", doctorId: "dr-007", doctorName: "Dr Omar CHRAIBI", doctorSpecialty: "IMAG", type: "consultation", startedAt: "2026-06-19 08:30", expectedDurationMin: 30, status: "SIGNED", version: 6, hasConflict: false, events: 12, lastUpdated: "2026-06-19 09:05" },
];

const TYPE_TONE: Record<Consultation["type"], "default" | "secondary" | "destructive" | "outline"> = {
  consultation: "default",
  surgery: "destructive",
  eeg: "secondary",
  "follow-up": "outline",
  emergency: "destructive",
};

const STATUS_TONE: Record<Consultation["status"], "default" | "secondary" | "destructive" | "outline"> = {
  DRAFT: "outline",
  IN_PROGRESS: "default",
  SIGNED: "secondary",
  AMENDED: "outline",
  CANCELLED: "destructive",
};

function ConsultationsAdmin() {
  const [search, setSearch] = useState("");
  const [filterConflict, setFilterConflict] = useState<"all" | "conflict" | "lock">("all");

  const filtered = useMemo(() => CONSULTATIONS.filter((c) => {
    if (filterConflict === "conflict" && !c.hasConflict) return false;
    if (filterConflict === "lock" && !c.lockedBy) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return c.patientName.toLowerCase().includes(s) || c.patientMrn.toLowerCase().includes(s) || c.doctorName.toLowerCase().includes(s);
  }), [search, filterConflict]);

  const conflicts = useMemo(() => {
    // Group by patient with multiple active consultations
    const byPatient: Record<string, Consultation[]> = {};
    for (const c of CONSULTATIONS) {
      if (c.status !== "IN_PROGRESS") continue;
      byPatient[c.patientMrn] = byPatient[c.patientMrn] ?? [];
      byPatient[c.patientMrn].push(c);
    }
    return Object.values(byPatient).filter((arr) => arr.length > 1);
  }, []);

  const stats = useMemo(() => ({
    total: CONSULTATIONS.length,
    inProgress: CONSULTATIONS.filter((c) => c.status === "IN_PROGRESS").length,
    conflict: CONSULTATIONS.filter((c) => c.hasConflict).length,
    locked: CONSULTATIONS.filter((c) => c.lockedBy).length,
  }), []);

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-2">
        <Badge variant="destructive" className="text-[10px]">SUPERADMIN</Badge>
        <span className="text-sm text-muted-foreground">· Concurrence, optimistic locking, atomicité</span>
      </div>

      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Activity className="h-5 w-5" /> Consultations atomiques
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Optimistic locking · Un patient peut avoir plusieurs consultations simultanées avec différents praticiens
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Total aujourd'hui</div>
            <div className="mt-1 text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">En cours</div>
            <div className="mt-1 text-2xl font-bold text-emerald-400">{stats.inProgress}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Concurrence (multi-praticien)</div>
            <div className="mt-1 text-2xl font-bold text-amber-400">{stats.conflict}</div>
            <div className="text-xs text-muted-foreground">même patient</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Lock explicite</div>
            <div className="mt-1 text-2xl font-bold text-red-400">{stats.locked}</div>
            <div className="text-xs text-muted-foreground">accès restreint</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="concurrent">
        <TabsList>
          <TabsTrigger value="concurrent"><Users className="h-3.5 w-3.5 mr-1" /> Concurrence ({conflicts.length})</TabsTrigger>
          <TabsTrigger value="all"><Activity className="h-3.5 w-3.5 mr-1" /> Toutes ({CONSULTATIONS.length})</TabsTrigger>
          <TabsTrigger value="pattern"><GitBranch className="h-3.5 w-3.5 mr-1" /> Pattern atomicité</TabsTrigger>
        </TabsList>

        {/* CONCURRENT TAB */}
        <TabsContent value="concurrent" className="space-y-2">
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
            <div className="flex items-center gap-2 font-semibold text-amber-300">
              <AlertTriangle className="h-4 w-4" /> Consultations concurrentes sur un même patient
            </div>
            <div className="text-amber-200/80 text-xs mt-1">
              L'atomicité est garantie par <strong>version vector</strong> et <strong>optimistic locking</strong> sur l'entité consultation.
              Chaque consultation est un brouillon indépendant — le merge/commit vérifie l'absence de conflit.
              Si une sous-ressource (patient.allergies) est modifiée par les 2 consultations, le 2e save détecte la divergence
              et propose une résolution manuelle.
            </div>
          </div>

          {conflicts.map((group) => (
            <Card key={group[0].patientMrn} className="border-amber-500/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <User className="h-4 w-4" /> {group[0].patientName}
                  <Badge variant="outline" className="text-[10px] font-mono">{group[0].patientMrn}</Badge>
                  <Badge variant="secondary" className="text-[10px] ml-auto">{group.length} consultations simultanées</Badge>
                </CardTitle>
                <CardDescription>
                  {group.map((c) => `${c.doctorName} (${SPECIALTY_LABEL[c.doctorSpecialty]}) — ${c.type}`).join(" · ")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {group.map((c) => (
                  <div key={c.id} className="rounded-md border border-white/5 bg-white/[0.02] p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Stethoscope className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-medium text-sm">{c.doctorName}</span>
                      <Badge variant="outline" className="text-[10px]">{SPECIALTY_LABEL[c.doctorSpecialty]}</Badge>
                      <Badge variant={TYPE_TONE[c.type]} className="text-[10px]">{c.type}</Badge>
                      <Badge variant={STATUS_TONE[c.status]} className="text-[10px]">{c.status}</Badge>
                      <Badge variant="secondary" className="text-[10px] font-mono ml-auto">v{c.version}</Badge>
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Démarrée {c.startedAt} · {c.expectedDurationMin}min · {c.events} sous-événements saga
                      {c.lockedBy && <span className="ml-2 text-red-400"><Lock className="h-3 w-3 inline" /> Lock par {MOCK_DOCTORS.find((d) => d.id === c.lockedBy)?.name}</span>}
                    </div>
                  </div>
                ))}
                <div className="rounded-md border border-emerald-500/20 bg-emerald-500/5 p-2 text-xs text-emerald-300 flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Atomicité garantie : chaque consultation est un sous-graphe indépendant du patient
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ALL TAB */}
        <TabsContent value="all" className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Rechercher (patient, MRN, médecin)..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Button size="sm" variant={filterConflict === "all" ? "default" : "outline"} onClick={() => setFilterConflict("all")}>Tous</Button>
            <Button size="sm" variant={filterConflict === "conflict" ? "default" : "outline"} onClick={() => setFilterConflict("conflict")}>Concurrents</Button>
            <Button size="sm" variant={filterConflict === "lock" ? "default" : "outline"} onClick={() => setFilterConflict("lock")}>Lockés</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-muted-foreground">
                    <th className="text-left p-2 text-xs uppercase">ID</th>
                    <th className="text-left p-2 text-xs uppercase">Patient</th>
                    <th className="text-left p-2 text-xs uppercase">Médecin</th>
                    <th className="text-left p-2 text-xs uppercase">Type</th>
                    <th className="text-left p-2 text-xs uppercase">Statut</th>
                    <th className="text-right p-2 text-xs uppercase">Ver.</th>
                    <th className="text-right p-2 text-xs uppercase">Events</th>
                    <th className="text-right p-2 text-xs uppercase">Maj</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="p-2 font-mono text-xs">
                        {c.id}
                        {c.hasConflict && <AlertTriangle className="h-3 w-3 text-amber-400 inline ml-1" />}
                        {c.lockedBy && <Lock className="h-3 w-3 text-red-400 inline ml-1" />}
                      </td>
                      <td className="p-2">
                        <div className="text-sm">{c.patientName}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{c.patientMrn}</div>
                      </td>
                      <td className="p-2 text-xs">{c.doctorName}</td>
                      <td className="p-2"><Badge variant={TYPE_TONE[c.type]} className="text-[10px]">{c.type}</Badge></td>
                      <td className="p-2"><Badge variant={STATUS_TONE[c.status]} className="text-[10px]">{c.status}</Badge></td>
                      <td className="text-right p-2 font-mono text-xs">v{c.version}</td>
                      <td className="text-right p-2 font-mono text-xs">{c.events}</td>
                      <td className="text-right p-2 text-[10px] text-muted-foreground">{c.lastUpdated.slice(11)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PATTERN TAB */}
        <TabsContent value="pattern" className="space-y-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><GitBranch className="h-4 w-4" /> Pattern d'atomicité</CardTitle>
              <CardDescription>Comment l'atomicité est garantie dans DIKDOC</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Pattern
                step={1}
                title="Création"
                description="POST /api/consultations crée un brouillon avec version=1. Aucun lock partagé."
                code={`POST /api/consultations
{ patientId, doctorId, type, startedAt }
→ 201 { id, version: 1, status: "DRAFT" }`}
              />
              <Pattern
                step={2}
                title="Sous-événements"
                description="Chaque sous-événement (prescription, lab, FSE) est un step indépendant de la saga. Rollback partiel si un step échoue."
                code={`POST /api/consultations/{id}/events
{ type: "PRESCRIPTION", payload: {...} }
→ saga engine orchestre compensation si besoin`}
              />
              <Pattern
                step={3}
                title="Optimistic locking"
                description="PUT /api/consultations/{id} vérifie If-Match: version. Si mismatch → 409 Conflict."
                code={`PUT /api/consultations/c-001
If-Match: 3
{ status: "SIGNED" }
→ 200 (if version was 3)
→ 409 Conflict (if version was 4)`}
              />
              <Pattern
                step={4}
                title="Concurrence multi-praticien"
                description="Plusieurs consultations sur le même patient par différents médecins sont autorisées. Chaque consultation est un sous-graphe indépendant."
                code={`# Patient P1234
Consultation c-001 (Dr ANES)   ← write allergies, anesthesia notes
Consultation c-002 (Dr NEURO)  ← write neuro exam, EEG request
Consultation c-003 (Dr CARD)   ← write ECG, locked
→ 3 sous-graphes indépendants
→ merge automatique des writes sur patient.* (latest-wins, audit WORM)`}
              />
              <Pattern
                step={5}
                title="Lock explicite (rare)"
                description="Un médecin peut demander un lock explicite (ex. EEG qui monopolise l'attention). Refuse les writes concurrents sur les champs partagés."
                code={`POST /api/consultations/{id}/lock
{ ttlSeconds: 600 }
→ 200 { lockToken, expiresAt }
→ 409 Conflict si déjà locké`}
              />
              <Pattern
                step={6}
                title="Signe & amend"
                description="Une consultation signée est immutable. Les modifications passent par un amendement (nouvelle version)."
                code={`POST /api/consultations/{id}/amend
{ amendment: "added note" }
→ 200 { version: 7, parentVersion: 6, status: "AMENDED" }`}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Pattern({ step, title, description, code }: { step: number; title: string; description: string; code: string }) {
  return (
    <div className="rounded-md border border-white/5 bg-white/[0.02] p-3">
      <div className="flex items-center gap-2 mb-1">
        <Badge variant="outline" className="text-[10px] font-mono">{step}</Badge>
        <span className="font-medium">{title}</span>
      </div>
      <div className="text-xs text-muted-foreground mb-2">{description}</div>
      <pre className="text-[11px] bg-black/30 rounded p-2 overflow-x-auto font-mono whitespace-pre">{code}</pre>
    </div>
  );
}
